import React, {
  useEffect, useRef, useState
} from 'react';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import {
  Header, Container, HeaderTitle, Icon, Loading, Modal, Toast
} from '@/components';
import ChainbaseSwitch from '@/components/Switch';
import './index.scss';
import classNames from 'classnames';
import { Button, Skeleton } from '@douyinfe/semi-ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddCard from '@/views/settings/billing/AddCard';
import PlanList, { PlanItem } from '@/views/settings/billing/PlanList';
import RecentInvoiceTable from '@/views/settings/billing/RecentInvoiceTable';
import UpgradeModal from '@/views/settings/billing/UpgradeModal';
import { Response } from '@/model';
import {
  CreditExtraPurchaseResult,
  CreditPricing,
  CreditPricingCustomRule,
  CreditPricingTier,
  PaymentCardInfo
} from '@/model/BillingPlan';
import { settingBillingThunk } from '@/store/reducers/settingBillingReducer';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import TextHighlight from '@/components/Text/TextHighlight';
import { DateUtils } from '@/utils';
import { reportEvent } from '@/utils/useTrack';
import {
  BILLING_TAB_PARAM,
  BILLING_TABS,
  getBillingPath,
  getBillingTab
} from '../../../config/billing';

export declare interface SettingPanelProps {
  icon: string;
  name: string;
  children: React.ReactElement;
}

export function SettingPanel({ icon, name, children }: SettingPanelProps): React.ReactElement {
  return (
    <div className={classNames('setting-panel')}>
      <div className={classNames('head')}>
        <Icon className="icon" icon={icon} />
        <h5 className="name medium">{name}</h5>
      </div>
      {children}
    </div>
  );
}

interface PaymentMethodProps {
  card: PaymentCardInfo;
  onChange: () => void;
}

function Dot(): React.ReactElement {
  return <span className="dot" />;
}

function DotGroup(): React.ReactElement {
  return (
    <div className="dot-group">
      {_.times(4).map((index) => (
        <Dot key={`${index}`} />
      ))}
    </div>
  );
}

function PaymentMethod({ card: cardInfo, onChange }: PaymentMethodProps): React.ReactElement {
  const { t } = useTranslation(['settings', 'translation']);
  const currentPaymentInfo = useAppSelector((state) => state.settingBilling.cardInfo);

  const renderCardInfo = (): React.ReactElement => {
    if (cardInfo.last4) {
      return (
        <>
          {/* <Icon */}
          {/*  icon={`billing/${cardInfo?.brand?.toLowerCase()}-card`} */}
          {/*  className="icon" */}
          {/* /> */}
          <span className="brand">{cardInfo?.brand}</span>
          <div className="payment-card">
            {_.times(3).map((i) => (
              <DotGroup key={`${i}`} />
            ))}
            <span className="card-number">{cardInfo.last4}</span>
          </div>
          <span className="delimiter" />
          <span className="expires">
            {`${t('billing.payment.expires')}: `}
            {`${cardInfo.exp_month} / ${cardInfo.exp_year}`}
          </span>
        </>
      );
    }
    return <div className="empty-tip">{t('billing.detail.empty')}</div>;
  };

  return (
    <div className={classNames('payment-method')}>
      <Skeleton placeholder={<Skeleton.Title />} loading={!currentPaymentInfo}>
        {renderCardInfo()}
      </Skeleton>
      <Button theme="solid" className="btn-change" onClick={onChange}>
        {t('common.edit', { ns: 'translation' })}
      </Button>
    </div>
  );
}

function getCurrentPlanName(planList: PlanItem[], subscriptionPlan: number | undefined, fallback: string): string {
  if (subscriptionPlan === undefined) {
    return fallback;
  }

  return _.find(
    planList,
    (item: PlanItem) => item.id === subscriptionPlan || item.activeIds.includes(subscriptionPlan)
  )?.name || fallback;
}

function PlanExpiresTip(): React.ReactElement {
  const { t } = useTranslation('settings');

  const currentPaymentInfo = useAppSelector((state) => state.settingBilling.current);
  if (currentPaymentInfo?.subscription_plan === currentPaymentInfo?.next_subscription_plan) {
    return <div />;
  }

  const planList = t('billing.plan.list', { returnObjects: true }) as PlanItem[];
  const currentPlanName = getCurrentPlanName(planList, currentPaymentInfo?.subscription_plan, '');

  const endDate = DateUtils.format(currentPaymentInfo?.current_period_end || 0);
  return (
    <div className="plan-expires-tip">
      <TextHighlight
        searchWords={[endDate]}
        textToHighlight={t('billing.planExpires')
          .replace('{plan}', currentPlanName)
          .replace('{end}', endDate)}
        highlightClassName={classNames('medium')}
        highlightStyle={{
          color: '#000000'
        }}
      />
    </div>
  );
}

export function PowerByStripeTitle({ children }: any): React.ReactElement {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span style={{ fontWeight: 600 }}>{children}</span>
      <span
        style={{
          fontSize: '14px',
          lineHeight: '16px',
          marginLeft: '18px',
          color: '#CACDD0',
          marginRight: '6px'
        }}
      >
        Powered by
      </span>
      <Icon icon="pricing/stripe" />
    </div>
  );
}

export declare type PlanUpgradeDirection = 'upgrade' | 'downgrade';

type ExtraCreditsOptionId = number | 'custom';

const usdWholeFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const numberFormatter = new Intl.NumberFormat('en-US');

function formatIntegerString(amount: string): string {
  const isNegative = amount.startsWith('-');
  const normalizedAmount = amount.replace('-', '');

  const formattedAmount = normalizedAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return isNegative ? `-${formattedAmount}` : formattedAmount;
}

function parseCreditAmount(amount: string | number | undefined): number {
  if (amount === undefined) {
    return 0;
  }

  return Number(amount) || 0;
}

function isUnlimitedCredits(creditTotal?: string): boolean {
  return creditTotal === '-1';
}

function getCreditUsagePercent(creditUsed?: string, creditTotal?: string): number {
  if (!creditUsed || !creditTotal || isUnlimitedCredits(creditTotal)) {
    return 0;
  }

  const total = parseCreditAmount(creditTotal);
  const used = parseCreditAmount(creditUsed);

  if (total <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, Math.round((used / total) * 100)));
}

function getMatchingCustomRule(amount: number, creditPricing?: CreditPricing): CreditPricingCustomRule | undefined {
  return creditPricing?.custom_rules.find((item) => amount >= item.amount_usd);
}

function getExtraCreditsEstimate(
  amount: number,
  creditPricing?: CreditPricing,
  matchingRule?: CreditPricingCustomRule
): number {
  if (!creditPricing || amount <= 0) {
    return 0;
  }

  const rule = matchingRule || getMatchingCustomRule(amount, creditPricing);
  const discountPct = rule?.discount_pct || 0;

  return Math.floor((amount * creditPricing.unit_credits_per_usd * (100 + discountPct)) / 100);
}

function formatUsd(amount: number): string {
  return usdFormatter.format(amount);
}

function formatUsdWhole(amount: number): string {
  return usdWholeFormatter.format(amount);
}

function formatCredits(amount: number | string): string {
  if (typeof amount === 'string') {
    return formatIntegerString(amount);
  }

  return numberFormatter.format(amount);
}

interface PaymentRequest {
  mode: 'pay' | 'change' | PlanUpgradeDirection;
  plan?: PlanItem;
}

type BuyExtraCreditsModalProps = {
  card?: PaymentCardInfo;
  onClose: () => void;
  onChangePaymentMethod: () => void;
}

function BuyExtraCreditsModal({
  card,
  onClose,
  onChangePaymentMethod
}: BuyExtraCreditsModalProps): React.ReactElement {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['settings', 'translation']);
  const [selectedOptionId, setSelectedOptionId] = useState<ExtraCreditsOptionId>('custom');
  const [customAmount, setCustomAmount] = useState('300');
  const [submitting, setSubmitting] = useState(false);
  const customAmountInputRef = useRef<HTMLInputElement>(null);
  const creditPricing = useAppSelector((state) => state.settingBilling.creditPricing);

  const customAmountValue = Number.parseInt(customAmount, 10) || 0;
  const selectedOption = typeof selectedOptionId === 'number'
    ? creditPricing?.tiers.find((item) => item.amount_usd === selectedOptionId)
    : undefined;
  const matchingRule = selectedOption ? undefined : getMatchingCustomRule(customAmountValue, creditPricing);

  const price = selectedOption ? selectedOption.amount_usd : customAmountValue;
  const credits = selectedOption
    ? selectedOption.credits
    : getExtraCreditsEstimate(customAmountValue, creditPricing, matchingRule);
  const discountLabel = selectedOption?.label || matchingRule?.label || '';
  const amountInRange = creditPricing
    ? price >= creditPricing.min_amount_usd && price <= creditPricing.max_amount_usd
    : false;
  const hasCard = Boolean(card?.last4);

  useEffect(() => {
    if (selectedOptionId === 'custom') {
      customAmountInputRef.current?.focus();
      customAmountInputRef.current?.select();
    }
  }, [selectedOptionId]);

  useEffect(() => {
    if (selectedOptionId !== 'custom' && !creditPricing?.tiers.some((item) => item.amount_usd === selectedOptionId)) {
      setSelectedOptionId('custom');
    }
  }, [creditPricing, selectedOptionId]);

  const handlePurchase = async () => {
    if (!amountInRange || !creditPricing || submitting) {
      return;
    }

    setSubmitting(true);
    const action = await dispatch(settingBillingThunk.buyExtraCredits({ amountUsd: price }));
    const response = action?.payload as AxiosResponse<Response<CreditExtraPurchaseResult>> | undefined;

    if (response?.data?.code === 200) {
      await dispatch(settingBillingThunk.getCreditStatus());
      onClose();
    } else if (response) {
      Toast.toastResponse(response);
    } else {
      Toast.error(('error' in action && action.error.message) || 'Request failed');
    }

    setSubmitting(false);
  };

  return (
    <div className="extra-credits-modal__content">
      <p className="extra-credits-modal__description">{t('billing.credit.extraCredits.modal.description')}</p>

      <div className="extra-credits-modal__options" role="listbox" aria-label={t('billing.credit.extraCredits.modal.title')}>
        {(creditPricing?.tiers || []).map((option: CreditPricingTier) => {
          const isSelected = selectedOptionId === option.amount_usd;

          return (
            <button
              key={option.amount_usd}
              type="button"
              className={classNames('extra-credits-option', {
                'extra-credits-option--selected': isSelected
              })}
              onClick={() => setSelectedOptionId(option.amount_usd)}
            >
              <span className="extra-credits-option__amount">{formatUsdWhole(option.amount_usd)}</span>
              <span className="extra-credits-option__credits">{`${formatCredits(option.credits)} credits`}</span>
              <span className="extra-credits-option__badge">{option.label}</span>
            </button>
          );
        })}

        <button
          type="button"
          className={classNames('extra-credits-option', 'extra-credits-option--custom', {
            'extra-credits-option--selected': selectedOptionId === 'custom'
          })}
          onClick={() => setSelectedOptionId('custom')}
        >
          <span className="extra-credits-option__amount">{t('billing.credit.extraCredits.modal.other')}</span>
        </button>
      </div>

      {selectedOptionId === 'custom' ? (
        <label className="extra-credits-modal__custom-input" htmlFor="extra-credits-custom-amount">
          <div className="extra-credits-modal__custom-main">
            <div className="extra-credits-modal__custom-amount-row">
              <span className="extra-credits-modal__custom-prefix">$</span>
              <input
                ref={customAmountInputRef}
                id="extra-credits-custom-amount"
                type="number"
                min={creditPricing?.min_amount_usd || 1}
                max={creditPricing?.max_amount_usd || 10000}
                step="1"
                inputMode="numeric"
                value={customAmount}
                onChange={(event) => setCustomAmount(event.target.value)}
              />
            </div>
            <span className="extra-credits-modal__custom-credits">{`${formatCredits(credits)} credits`}</span>
          </div>
          {discountLabel ? (
            <span className="extra-credits-option__badge extra-credits-option__badge--inline">
              {discountLabel}
            </span>
          ) : null}
        </label>
      ) : null}

      <button
        type="button"
        className="extra-credits-modal__pricing-link"
        onClick={() => window.open('https://docs.chainbase.com/platform/overview', '_blank', 'noreferrer')}
      >
        {t('billing.credit.extraCredits.modal.learnMorePricing')}
      </button>

      <div className="extra-credits-modal__summary">
        <div className="extra-credits-modal__summary-row">
          <span>{t('billing.credit.extraCredits.modal.price')}</span>
          <span>{`USD ${formatUsd(price).replace('$', '')}`}</span>
        </div>
        <div className="extra-credits-modal__summary-row">
          <span>{t('billing.credit.extraCredits.modal.credits')}</span>
          <span>{formatCredits(credits)}</span>
        </div>
        <div className="extra-credits-modal__summary-row">
          <span>{t('billing.credit.extraCredits.modal.discount')}</span>
          <span>{discountLabel || '0%'}</span>
        </div>
        <div className="extra-credits-modal__summary-divider" />
        <div className="extra-credits-modal__summary-row extra-credits-modal__summary-row--total">
          <span>{t('billing.credit.extraCredits.modal.totalDue')}</span>
          <span>{`USD ${formatUsd(price).replace('$', '')}`}</span>
        </div>
      </div>

      <div className="extra-credits-modal__payment-method">
        <div className="extra-credits-modal__payment-copy">
          <span className="extra-credits-modal__payment-last4">
            {hasCard ? card?.last4 : t('billing.credit.extraCredits.modal.noPaymentMethod')}
          </span>
          <span className="extra-credits-modal__payment-brand">{hasCard ? card?.brand : ''}</span>
        </div>
        <button type="button" className="extra-credits-modal__payment-action" onClick={onChangePaymentMethod}>
          {hasCard
            ? t('billing.credit.extraCredits.modal.editPaymentMethod')
            : t('billing.credit.extraCredits.modal.addPaymentMethod')}
        </button>
      </div>

      <Button
        theme="solid"
        type="primary"
        className="extra-credits-modal__pay-button"
        disabled={!amountInRange || price <= 0 || !hasCard || submitting}
        onClick={handlePurchase}
      >
        <span className="extra-credits-modal__pay-content">
          {submitting ? <Loading type="primary" className="extra-credits-modal__pay-loader" /> : null}
          <span>{t('billing.credit.extraCredits.modal.payCta', { amount: formatUsd(price) })}</span>
        </span>
      </Button>
    </div>
  );
}

type CreditUsageCardProps = {
  used: string;
  total: string;
  usageLabel: string;
  progress: number;
}

function CreditUsageCard({
  used,
  total,
  usageLabel,
  progress
}: CreditUsageCardProps): React.ReactElement {
  return (
    <div className="credit-usage-card">
      <div className="credit-usage-card__row">
        <div className="credit-usage-card__values">
          <span className="credit-usage-card__used">{used}</span>
          <span className="credit-usage-card__total">
            /
            {total}
          </span>
        </div>
        <div className="credit-usage-card__progress" aria-hidden="true">
          <div
            className="credit-usage-card__progress-bar"
            style={{ width: `${Math.max(0, Math.min(progress, 100))}%` }}
          />
        </div>
        <span className="credit-usage-card__usage">{usageLabel}</span>
      </div>
    </div>
  );
}

type CreditSectionProps = {
  title: string;
  ctaLabel: string;
  learnMoreLabel: string;
  learnMoreUrl: string;
  onCtaClick: () => void;
  children: React.ReactNode;
}

function CreditSection({
  title,
  ctaLabel,
  learnMoreLabel,
  learnMoreUrl,
  onCtaClick,
  children
}: CreditSectionProps): React.ReactElement {
  return (
    <section className="credit-section">
      <div className="credit-section__header">
        <div className="credit-section__heading-row">
          <h3 className="credit-section__title medium">{title}</h3>
          <button type="button" className="credit-section__cta" onClick={onCtaClick}>
            {ctaLabel}
          </button>
        </div>
        <button type="button" className="credit-section__learn-more" onClick={() => window.open(learnMoreUrl, '_blank', 'noreferrer')}>
          <span>{learnMoreLabel}</span>
        </button>
      </div>
      {children}
    </section>
  );
}

export default function Billing(): React.ReactElement {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { t } = useTranslation(['settings', 'translation']);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | undefined>(undefined);
  const [isBuyExtraCreditsOpen, setIsBuyExtraCreditsOpen] = useState(false);
  const [isExtraCreditsToggleLoading, setIsExtraCreditsToggleLoading] = useState(false);
  const paymentCard = useAppSelector((state) => state.settingBilling.cardInfo);
  const currentPlan = useAppSelector((state) => state.settingBilling.current);
  const creditStatus = useAppSelector((state) => state.settingBilling.creditStatus);
  const activeTab = getBillingTab(searchParams.get(BILLING_TAB_PARAM));
  const isCreditTab = activeTab === BILLING_TABS.credit;
  const planList = t('billing.plan.list', { returnObjects: true }) as PlanItem[];
  const currentPlanName = getCurrentPlanName(
    planList,
    currentPlan?.subscription_plan,
    t('billing.credit.currentPlanFallback')
  );
  const isDeveloperPlan = currentPlan?.subscription_plan === 1;
  const isUnlimitedPlan = isUnlimitedCredits(creditStatus?.plan.credit_total);
  const planUsagePercent = getCreditUsagePercent(creditStatus?.plan.credit_used, creditStatus?.plan.credit_total);
  const extraUsagePercent = getCreditUsagePercent(creditStatus?.extra?.credit_used, creditStatus?.extra?.credit_total);
  const allowExtraCredits = creditStatus?.extra?.enable_extra || false;
  const planUsed = formatCredits(creditStatus?.plan.credit_used || '0');
  const planTotal = isUnlimitedPlan ? t('billing.credit.unlimited') : formatCredits(creditStatus?.plan.credit_total || '0');
  const extraUsed = formatCredits(creditStatus?.extra?.credit_used || '0');
  const extraTotal = formatCredits(creditStatus?.extra?.credit_total || '0');

  useEffect(() => {
    dispatch(settingBillingThunk.getCurrentPaymentInfo());
    dispatch(settingBillingThunk.getBindCardInfo());
  }, [dispatch]);

  useEffect(() => {
    if (isCreditTab) {
      dispatch(settingBillingThunk.getCreditStatus());
      dispatch(settingBillingThunk.getCreditPricing());
    }
  }, [dispatch, isCreditTab]);

  useEffect(() => {
    if (isCreditTab && isUnlimitedPlan) {
      navigate(getBillingPath(BILLING_TABS.planBilling), { replace: true });
    }
  }, [isCreditTab, isUnlimitedPlan, navigate]);

  const handleToggleExtraCredits = async (enable: boolean) => {
    setIsExtraCreditsToggleLoading(true);
    const action = await dispatch(settingBillingThunk.toggleExtraCredits({ enable }));
    const response = action?.payload as AxiosResponse<Response<null>> | undefined;

    if (response?.data?.code === 200) {
      await dispatch(settingBillingThunk.getCreditStatus());
    } else if (response) {
      Toast.toastResponse(response);
    } else {
      Toast.error(('error' in action && action.error.message) || 'Request failed');
    }

    setIsExtraCreditsToggleLoading(false);
  };

  const renderModal = (): React.ReactNode => {
    switch (paymentRequest?.mode) {
      case 'pay':
        return (
          <Modal
            title={<PowerByStripeTitle>{`${t('billing.payment.title')}`}</PowerByStripeTitle>}
            visible
            centered
            maskClosable={false}
            className={classNames('card-modal', 'payment-modal')}
            onCancel={() => setPaymentRequest(undefined)}
            footer={null}
            closeOnEsc={false}
          >
            <AddCard
              payButton={t('common.confirm', { ns: 'translation' })}
              plan={paymentRequest.plan}
              onClose={() => {
                dispatch(settingBillingThunk.getBindCardInfo());
                dispatch(settingBillingThunk.getCurrentPaymentInfo());
                setPaymentRequest(undefined);
              }}
              onBindSuccess={() => {
                reportEvent('cardBinded', {});
              }}
            />
          </Modal>
        );
      case 'change':
        return (
          <Modal
            title={<PowerByStripeTitle>{`${t('billing.payment.title')}`}</PowerByStripeTitle>}
            visible
            centered
            maskClosable={false}
            className={classNames('card-modal', 'payment-modal')}
            onCancel={() => setPaymentRequest(undefined)}
            footer={null}
            closeOnEsc={false}
          >
            <AddCard
              payButton={t('billing.addCard.changeCard')}
              plan={undefined}
              onClose={() => {
                dispatch(settingBillingThunk.getBindCardInfo());
                setPaymentRequest(undefined);
              }}
              onBindSuccess={() => {
                reportEvent('cardBinded', {});
              }}
              last4={paymentCard?.last4}
            />
          </Modal>
        );
      case 'upgrade':
      case 'downgrade':
        if (!paymentRequest.plan) {
          return null;
        }

        return (
          <Modal
            title={
              <span style={{ fontWeight: 600 }}>{t(`common.${paymentRequest?.mode}`, { ns: 'translation' })}</span>
            }
            visible
            centered
            onCancel={() => setPaymentRequest(undefined)}
            className={classNames('payment-modal')}
            footer={null}
          >
            <UpgradeModal
              mode={paymentRequest.mode}
              plan={paymentRequest.plan}
              onBindPaymentCard={() => {
                setPaymentRequest({
                  mode: 'pay',
                  plan: paymentRequest?.plan
                });
              }}
              onClose={() => setPaymentRequest(undefined)}
            />
          </Modal>
        );
      default:
        return null;
    }
  };

  return (
    <Container className="settings-container usage-container">
      <HeaderTitle title={isCreditTab ? t('billing.credit.headerTitle') : t('billing.headerTitle')} />
      {!isCreditTab ? <Header>{t('billing.headerTitle')}</Header> : null}

      {isCreditTab ? (
        <div className="credit-tab-content">
          <CreditSection
            title={t('billing.credit.planCredits.title')}
            ctaLabel={t('common.upgrade', { ns: 'translation' })}
            learnMoreLabel={t('billing.credit.planCredits.learnMore')}
            learnMoreUrl="https://docs.chainbase.com/resources/platform/credit/overview#plans-&-monthly-allowance"
            onCtaClick={() => navigate('/settings/billing')}
          >
            <div className="credit-section__meta">
              <div className="credit-section__current-plan">
                <span className="credit-section__current-plan-label">{t('billing.credit.currentPlanLabel')}</span>
                <span className="credit-section__current-plan-value medium">{currentPlanName}</span>
              </div>
            </div>

            <CreditUsageCard
              used={planUsed}
              total={planTotal}
              usageLabel={isUnlimitedPlan ? t('billing.credit.unlimited') : t('billing.credit.usageLabel', { percent: planUsagePercent })}
              progress={planUsagePercent}
            />

            {creditStatus?.plan.reset_time ? (
              <p className="credit-section__reset">{t('billing.credit.planCredits.reset', { date: DateUtils.format(creditStatus.plan.reset_time) })}</p>
            ) : null}
          </CreditSection>

          {isDeveloperPlan ? (
            <CreditSection
              title={t('billing.credit.extraCredits.title')}
              ctaLabel={t('billing.credit.extraCredits.buyCta')}
              learnMoreLabel={t('billing.credit.extraCredits.learnMore')}
              learnMoreUrl="https://docs.chainbase.com/resources/platform/credit/overview#extra-credits-usage"
              onCtaClick={() => setIsBuyExtraCreditsOpen(true)}
            >
              <div className="credit-section__stack">
                <CreditUsageCard
                  used={extraUsed}
                  total={extraTotal}
                  usageLabel={t('billing.credit.usageLabel', { percent: extraUsagePercent })}
                  progress={extraUsagePercent}
                />

                <div className="credit-section__toggle-row">
                  <ChainbaseSwitch
                    checked={allowExtraCredits}
                    disabled={!creditStatus?.extra || isExtraCreditsToggleLoading}
                    onChange={handleToggleExtraCredits}
                  />
                  <span className="credit-section__toggle-copy">{t('billing.credit.extraCredits.toggle')}</span>
                </div>
              </div>
            </CreditSection>
          ) : null}
        </div>
      ) : (
        <>
          <PlanExpiresTip />

          <SettingPanel icon="settings/plan" name={t('billing.plan.title')}>
            <PlanList
              onPayment={(item, direction) => {
                setPaymentRequest({
                  mode: direction,
                  plan: item
                });
              }}
            />
          </SettingPanel>

          {paymentCard ? (
            <SettingPanel icon="settings/payment" name={t('billing.payment.title')}>
              <PaymentMethod
                card={paymentCard}
                onChange={() => setPaymentRequest({
                  mode: 'change'
                })}
              />
            </SettingPanel>
          ) : null}

          <SettingPanel icon="settings/billing" name={t('billing.billing.title')}>
            <RecentInvoiceTable key={`${currentPlan?.next_subscription_plan}`} />
          </SettingPanel>
        </>
      )}

      {renderModal()}

      <Modal
        title={t('billing.credit.extraCredits.modal.title')}
        visible={isBuyExtraCreditsOpen}
        centered
        width={700}
        closeIcon={(
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M14.8335 4.22754L10.0601 9L14.8335 13.7734L13.7729 14.834L8.99951 10.0605L4.22705 14.834L3.1665 13.7734L7.93896 9L3.1665 4.22754L4.22705 3.16699L8.99951 7.93945L13.7729 3.16699L14.8335 4.22754Z" fill="#A1AAB0" />
          </svg>
        )}
        className={classNames('payment-modal', 'extra-credits-modal')}
        onCancel={() => setIsBuyExtraCreditsOpen(false)}
        footer={null}
      >
        <BuyExtraCreditsModal
          card={paymentCard}
          onClose={() => setIsBuyExtraCreditsOpen(false)}
          onChangePaymentMethod={() => {
            setIsBuyExtraCreditsOpen(false);
            setPaymentRequest({
              mode: 'change'
            });
          }}
        />
      </Modal>
    </Container>
  );
}
